// src/lib/embed/cookies.ts
//
// SameSite/Secure/Partitioned attributes for cookies written from
// `document.cookie` (src/lib/consent.ts, src/lib/fingerprint-collect.ts).
//
// Both writers use SameSite=Lax today, which a browser never sends on a
// cross-site request — including every request made inside a cross-site
// <iframe>, which is exactly what embedding our checkout on a merchant's own
// site is. Framed same-site (or not framed at all) keeps today's behaviour
// untouched, including each caller's own Secure decision. Framed cross-site
// needs SameSite=None, which a browser rejects outright without Secure, and
// which this app additionally partitions (CHIPS) so the cookie jar doesn't
// leak across merchants sharing the same embed. Framed cross-site over plain
// http (local dev) has no safe SameSite=None form — Secure can't be set over
// http — so it falls back to SameSite=Lax: a cookie a browser silently drops
// is worse than one that simply isn't sent on the first cross-site request.
export type EmbedFramingContext = {
  framed: boolean;
  crossSite: boolean;
  https: boolean;
};

/**
 * The attribute string to append after `Path=...;` in a `document.cookie`
 * write. `secureWhenNotEmbedded` is the caller's own, pre-existing Secure
 * decision for the non-cross-site-framed case — consent.ts secures only over
 * https, fingerprint-collect.ts never does — preserved here rather than
 * decided by this helper, so today's behaviour for that branch is unchanged
 * byte for byte.
 */
export function embedCookieAttributes(
  ctx: EmbedFramingContext,
  secureWhenNotEmbedded: boolean,
): string {
  if (ctx.framed && ctx.crossSite && ctx.https) {
    return "SameSite=None; Secure; Partitioned";
  }
  return `SameSite=Lax${secureWhenNotEmbedded ? "; Secure" : ""}`;
}

/**
 * Detect the current page's framing for `embedCookieAttributes`. Browser-only.
 *
 * shortcut: any failure reading `window.top.location` is treated as
 * cross-site, which also flags a same-site-but-cross-origin parent (e.g. a
 * subdomain iframe) as cross-site. Nothing in this app frames across
 * subdomains today, so the distinction doesn't matter yet — upgrade this if
 * that changes.
 */
export function detectEmbedFraming(): EmbedFramingContext {
  if (typeof window === "undefined") return { framed: false, crossSite: false, https: false };

  const https = window.location.protocol === "https:";
  let framed: boolean;
  try {
    framed = window.self !== window.top;
  } catch {
    framed = true;
  }
  if (!framed) return { framed: false, crossSite: false, https };

  try {
    // Throws a SecurityError for a cross-origin parent; same-origin access succeeds.
    void window.top?.location.href;
    return { framed: true, crossSite: false, https };
  } catch {
    return { framed: true, crossSite: true, https };
  }
}
